import React, { useEffect, useState } from 'react';
import { changeRequests, followups } from '@/utils/api';
import { GitBranch, Plus, Check, X, Sparkles, Trash2, ClipboardCopy, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ChangeRequestsTab({ projectId, isOperator = true }) {
  const [list, setList] = useState([]);
  const [followupList, setFollowupList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', cost_delta: 0, timeline_delta_days: 0 });
  const [followReason, setFollowReason] = useState('client went quiet');
  const [draftingFollowup, setDraftingFollowup] = useState(false);

  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ cost_delta: 0, timeline_delta_days: 0, note: '' });

  const reload = () => {
    changeRequests.list(projectId).then(r => setList(r.data));
    if (isOperator) followups.list(projectId).then(r => setFollowupList(r.data));
  };
  useEffect(reload, [projectId]); // eslint-disable-line

  const save = async () => {
    try {
      // Clients only submit title + description. The operator sets cost/timeline
      // impact when they decide on the request. Operators can still propose one
      // themselves with an impact pre-filled via the same form.
      const payload = isOperator
        ? { project_id: projectId, ...form }
        : { project_id: projectId, title: form.title, description: form.description, cost_delta: 0, timeline_delta_days: 0 };
      await changeRequests.create(payload);
      toast.success('Change request created');
      setCreating(false);
      setForm({ title: '', description: '', cost_delta: 0, timeline_delta_days: 0 });
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const openApprove = (cr) => {
    setApproveFor(cr);
    setApproveForm({
      cost_delta: cr.cost_delta || 0,
      timeline_delta_days: cr.timeline_delta_days || 0,
      note: '',
    });
  };

  const confirmApprove = async () => {
    try {
      await changeRequests.decide(approveFor.id, 'approved', approveForm.note, {
        cost_delta: approveForm.cost_delta,
        timeline_delta_days: approveForm.timeline_delta_days,
      });
      toast.success('Approved');
      setApproveFor(null);
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const reject = async (id) => {
    const note = window.prompt('Reason (optional):') || '';
    await changeRequests.decide(id, 'rejected', note);
    toast.success('Marked rejected');
    reload();
  };

  const del = async (id) => {
    if (!window.confirm('Delete change request?')) return;
    await changeRequests.delete(id);
    reload();
  };

  const draftFollowup = async () => {
    setDraftingFollowup(true);
    try {
      await followups.draft(projectId, followReason);
      toast.success('Follow-up drafted');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setDraftingFollowup(false); }
  };

  return (
    <div className="p-4 animate-fade-in" data-testid="change-requests-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-black text-base flex items-center gap-2" style={{ color: 'var(--ink)' }}>
          <GitBranch size={16} /> Change Requests & Follow-ups
        </h2>
        <button className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5" onClick={() => setCreating(true)} data-testid="new-cr-button">
          <Plus size={12} /> New request
        </button>
      </div>

      {/* Change requests */}
      {list.length === 0 ? (
        <div className="card-base p-6 text-center mb-5">
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No change requests yet</p>
        </div>
      ) : (
        <div className="space-y-2 mb-6" data-testid="cr-list">
          {list.map(c => (
            <div key={c.id} className="card-base p-3" data-testid={`cr-${c.id}`}>
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{c.title}</p>
                    <span className="badge-status" style={{
                      background: c.status === 'approved' ? '#3C8A5A15' : c.status === 'rejected' ? '#B34A3A15' : '#D9A44115',
                      color: c.status === 'approved' ? '#3C8A5A' : c.status === 'rejected' ? '#B34A3A' : '#D9A441',
                    }}>{c.status}</span>
                  </div>
                  <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: 'var(--muted)' }}>{c.description}</p>
                  <div className="flex gap-3 mt-1.5 text-xs flex-wrap" style={{ color: 'var(--muted)' }}>
                    {c.status === 'approved' || c.cost_delta || c.timeline_delta_days ? (
                      <>
                        <span>💰 {c.cost_delta >= 0 ? '+' : ''}${(c.cost_delta || 0).toFixed(2)}</span>
                        <span>⏱ {c.timeline_delta_days >= 0 ? '+' : ''}{c.timeline_delta_days || 0}d</span>
                      </>
                    ) : c.status === 'pending' ? (
                      <span className="italic">Awaiting operator's cost &amp; timeline estimate</span>
                    ) : null}
                    <span>by {c.requested_by_name || 'user'}</span>
                  </div>
                </div>
                {c.status === 'pending' && isOperator && (
                  <div className="flex gap-1">
                    <button onClick={() => openApprove(c)} className="text-xs px-2 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A', color: 'white' }} data-testid={`cr-approve-${c.id}`}><Check size={10} /> Approve</button>
                    <button onClick={() => reject(c.id)} className="text-xs px-2 py-1 rounded-btn font-semibold" style={{ background: '#B34A3A', color: 'white' }} data-testid={`cr-reject-${c.id}`}><X size={10} /></button>
                  </div>
                )}
                {isOperator && <button onClick={() => del(c.id)} className="text-xs p-1 ml-1" style={{ color: '#B34A3A' }}><Trash2 size={11} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Follow-ups */}
      {isOperator && (
        <>
          <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
            <Sparkles size={13} style={{ color: '#5B2D8E' }} /> AI-drafted follow-ups
          </h3>
          <div className="flex gap-2 mb-3">
            <select className="input-agency text-sm flex-1" value={followReason} onChange={e => setFollowReason(e.target.value)} data-testid="followup-reason">
              <option value="client went quiet">Client went quiet</option>
              <option value="pending invoice">Pending invoice nudge</option>
              <option value="awaiting approval">Awaiting approval</option>
              <option value="check-in">Casual check-in</option>
            </select>
            <button onClick={draftFollowup} disabled={draftingFollowup} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid="draft-followup">
              <Sparkles size={11} /> {draftingFollowup ? 'Drafting…' : 'Draft'}
            </button>
          </div>

          {followupList.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--muted)' }}>No drafts yet.</p>
          ) : (
            <div className="space-y-2" data-testid="followup-list">
              {followupList.map(f => (
                <div key={f.id} className="card-base p-3" data-testid={`followup-${f.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock size={11} style={{ color: 'var(--muted)' }} />
                      <p className="text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>{f.reason}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { navigator.clipboard.writeText(f.draft); toast.success('Copied'); }} className="text-xs p-1" style={{ color: '#006080' }} data-testid={`copy-followup-${f.id}`}><ClipboardCopy size={11} /></button>
                      <button onClick={() => followups.delete(f.id).then(reload)} className="text-xs p-1" style={{ color: '#B34A3A' }}><Trash2 size={11} /></button>
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap text-xs font-sans" style={{ color: 'var(--ink)' }}>{f.draft}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <h3 className="font-black mb-1" style={{ color: 'var(--ink)' }}>New change request</h3>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
              {isOperator
                ? 'Propose a scope change with its cost and timeline impact.'
                : "Describe the change you'd like. Your operator will reply with the cost and timeline impact for you to approve."}
            </p>
            <div className="space-y-2">
              <input className="input-agency text-sm" placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="cr-title" />
              <textarea className="input-agency text-sm resize-none" rows={4} placeholder="Describe what's changing and why" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="cr-description" />
              {isOperator && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Cost delta ($)</label>
                    <input className="input-agency text-sm" type="number" step="0.01" placeholder="e.g. 500 or -200" value={form.cost_delta} onChange={e => setForm({...form, cost_delta: parseFloat(e.target.value || '0')})} data-testid="cr-cost-delta" />
                    <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Positive = extra cost. Negative = credit.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Timeline delta (days)</label>
                    <input className="input-agency text-sm" type="number" placeholder="e.g. 5 or -2" value={form.timeline_delta_days} onChange={e => setForm({...form, timeline_delta_days: parseInt(e.target.value || '0')})} data-testid="cr-timeline-delta" />
                    <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Positive = slip. Negative = gain.</p>
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setCreating(false)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
                <button className="flex-1 btn-primary py-2 text-sm" onClick={save} disabled={!form.title || !form.description} data-testid="cr-save">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operator approval modal — set cost/timeline impact before approving a client-raised CR */}
      {approveFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="cr-approve-modal">
          <div className="w-full max-w-md rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <h3 className="font-black mb-1" style={{ color: 'var(--ink)' }}>Approve change request</h3>
            <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--muted)' }}>"{approveFor.title}"</p>
            <p className="text-xs mb-3 p-2 rounded-btn" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>
              Your estimate will be recorded against this change and visible to the client.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Cost delta ($)</label>
                <input className="input-agency text-sm" type="number" step="0.01" value={approveForm.cost_delta} onChange={e => setApproveForm({...approveForm, cost_delta: parseFloat(e.target.value || '0')})} data-testid="approve-cost-delta" autoFocus />
                <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Positive = extra cost. Negative = credit.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Timeline delta (days)</label>
                <input className="input-agency text-sm" type="number" value={approveForm.timeline_delta_days} onChange={e => setApproveForm({...approveForm, timeline_delta_days: parseInt(e.target.value || '0')})} data-testid="approve-timeline-delta" />
                <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Positive = slip. Negative = gain.</p>
              </div>
            </div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Note (optional)</label>
            <textarea className="input-agency text-sm resize-none mb-3" rows={2} placeholder="Any context for the client" value={approveForm.note} onChange={e => setApproveForm({...approveForm, note: e.target.value})} data-testid="approve-note" />
            <div className="flex gap-2">
              <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setApproveFor(null)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
              <button className="flex-1 py-2 text-sm rounded-btn text-white font-semibold" style={{ background: '#3C8A5A' }} onClick={confirmApprove} data-testid="approve-submit">Approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
